import {batch} from 'react-redux';
import _ from 'lodash';

import {getFirstSelectedCard} from 'lib/cardAndTaskHelper';
import {setDefaultTipTouchOffDirectionTasks} from 'lib/PipettingProfileItemHelper';
import {isOnSpearBioTrough} from 'lib/labware/spearBioLabwareHelper';
import WellItem from 'models/wellItem';
import {validateMinVolumeAllowed} from 'lib/protocolHelper';
import {
  TaskType,
  TipChannelType,
  CardType,
  WellCount,
  TransformSelectionType,
  LabwareWellCount,
  TrayType,
  CardInvalidType,
  ProtocolEditorViewMode,
  Feedback,
} from '../../constants/dataEnum';

import * as cardStateAction from '../actions/cardStateAction';
import * as cardBroadcastStateAction from '../actions/cardBroadcastStateAction';
import * as labwareStateAction from '../actions/labwareStateAction';
import * as temporaryStateAction from '../actions/temporaryStateAction';
import * as taskListBusinessAction from './taskListBusinessAction';
import {convertRowToAlphabet, getNewCardName, generateCardIDAndTaskIDAndNumber} from './commonTaskListBusiness';
import {
  getMinTipPositionByLabwareId,
  restoreTipCountForCards,
  restoreTipCountForSingleCard,
} from './protocolManagementBusiness';
import {saveState} from './stateBusiness';

import {filterToArray, find} from '../../lib/objectHelper';
import {
  convertRowToNumeric,
  getQuadrant,
  getTaskGroupByTipCountAndLabware,
  getTaskGroups,
  sortTaskByWellPosition,
  isPositionOnTrough,
  isLabwareColumnBased,
  isColumnBasedSwipe,
  ensureWellRangeFitLabware,
} from '../../contents/ProtocolEditor/VirtualTray/labwareHelper';
import {setBroadcastTaskEditStep, setSelectedMenu, setVolumeModalOpen} from '../actions/protocolEditorAction';
import {showSnackbar} from '../actions/appStateAction';
import {resolveIntl} from './commonBusiness';
import {BROADCAST_STATE_BEFORE_EDIT} from '../../constants/temporaryStateType';
import {getBroadcastTaskByType} from '../../lib/broadcastHelper';
import {adjustTaskPipettingParametersByLabware, extractPipettingParameterFromTask} from './pipettingParameterBusiness';

const intl = resolveIntl();

export const checkInvalidBroadcastCard = (cardId) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const selectedCard = cards[cardId];

  const taskAspirate = Object.values(tasks).find(
    (e) => selectedCard?.tasks.includes(e.ID) && e.type === TaskType.ASPIRATE,
  );
  const taskDispense = Object.values(tasks).find(
    (e) => selectedCard?.tasks.includes(e.ID) && e.type === TaskType.DISPENSE,
  );

  if (taskAspirate && taskDispense && taskAspirate.volume && taskDispense.volume) {
    dispatch(cardStateAction.removeCardInvalidType(cardId));
  } else {
    dispatch(cardStateAction.addCardInvalidType(cardId, [CardInvalidType.UNFINISHED]));
  }
};

export const restoreBroadcastWizard = (showVolumeModal) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const unfinishedSdCard = Object.values(cards).find(
    (e) => e.type === CardType.BROADCAST && e.invalidTypes?.includes(CardInvalidType.UNFINISHED),
  );

  if (unfinishedSdCard) {
    const taskAspirate = Object.values(tasks).find(
      (e) => unfinishedSdCard?.tasks.includes(e.ID) && e.type === TaskType.ASPIRATE,
    );
    const taskDispense = Object.values(tasks).find(
      (e) => unfinishedSdCard?.tasks.includes(e.ID) && e.type === TaskType.DISPENSE,
    );

    dispatch(setSelectedMenu(ProtocolEditorViewMode.BROADCAST));
    if (taskAspirate?.volume === 0 && taskDispense?.volume === 0) {
      showVolumeModal();
    }
  }
};

const createBroadcastCard = (cardId, reuseTips) => async (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;

  const card = cards[cardId];

  if (card) {
    return;
  }

  const cardName = getNewCardName(cards, CardType.BROADCAST);

  await dispatch(cardStateAction.addCard(cardId, cardName, CardType.BROADCAST, reuseTips));
};

export const addBroadcastTask =
  (
    taskConfigList = [{type: '', labwareId: 0, row: '', column: '', rowTipCount: 1, columnTipCount: 1}],
    skipSaveState = false,
    newCardId = null,
    defaultIsReuseTips = null,
  ) =>
  (dispatch, getState) => {
    const {getWellTarget} = taskListBusinessAction;

    const {cardState, labwareState, temporaryState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = !newCardId ? find(cards, {isSelected: true}) : find(cards, {ID: newCardId});

    const existingTaskAspirate = find(tasks, {ID: selectedCard?.tasks, type: TaskType.ASPIRATE});
    let existingTaskDispense = find(tasks, {ID: selectedCard?.tasks, type: TaskType.DISPENSE});
    const taskEjectTip = find(tasks, {ID: selectedCard?.tasks, type: TaskType.EJECT_TIP});
    const taskPickTip = find(tasks, {ID: selectedCard?.tasks, type: TaskType.PICK_TIP});

    if (!existingTaskDispense && temporaryState.broadcastStateBeforeEdit) {
      const {broadcastStateBeforeEdit} = temporaryState;
      if (broadcastStateBeforeEdit) {
        const {cards: cardsTemp, tasks: tasksTemp} = broadcastStateBeforeEdit.cardState;
        const selectedCardTemp = !newCardId ? find(cardsTemp, {isSelected: true}) : find(cardsTemp, {ID: newCardId});

        existingTaskDispense = find(tasksTemp, {ID: selectedCardTemp?.tasks, type: TaskType.DISPENSE});
      }
    }

    /* eslint-disable-next-line no-nested-ternary */
    const isReuseTips =
      defaultIsReuseTips ?? (selectedCard && 'reuseTips' in selectedCard ? selectedCard.reuseTips : false);

    const taskListToAdd = [];
    const taskIdsToRemove = [];

    let cardId = newCardId ?? 0;
    let taskId;
    let number;

    [cardId, taskId, number] = generateCardIDAndTaskIDAndNumber(
      cards,
      tasks,
      CardType.BROADCAST,
      selectedCard?.ID,
      taskConfigList[0].type,
      null,
      {row: taskConfigList[0].rowTipCount, column: taskConfigList[0].columnTipCount},
    );

    const cardName = cards[cardId]?.name || getNewCardName(cards, CardType.BROADCAST);

    if (isReuseTips) {
      // sync aspirate task tip count
      if (existingTaskAspirate) {
        const newTaskAspirate = {
          ...existingTaskAspirate,
          columnTipCount: taskConfigList[0].columnTipCount,
          rowTipCount: taskConfigList[0].rowTipCount,
        };

        const allTask = filterToArray(tasks, {ID: selectedCard?.tasks});

        allTask.forEach((task) => {
          if (
            (task.type === TaskType.PICK_TIP && task.ID !== taskPickTip.ID) ||
            (task.type === TaskType.ASPIRATE && task.ID !== existingTaskAspirate.ID) ||
            (task.type === TaskType.EJECT_TIP && task.ID !== taskEjectTip.ID)
          ) {
            taskIdsToRemove.push(task.ID);
          }
        });

        dispatch(cardStateAction.restoreCardTaskStateById(newTaskAspirate));
      }
    }

    for (let index = 0; index < taskConfigList.length; index++) {
      const taskConfig = taskConfigList[index];
      const {type, labwareId, row, column, rowTipCount, columnTipCount} = taskConfig;

      const selectedLabware = find(labwareState, {LabwareID: labwareId});

      let startRow = row;
      let startColumn = column;

      if (selectedLabware) {
        const {rowCount: labwareRowCount, colCount: labwareColCount} = selectedLabware;

        const labwareWellCount = labwareColCount * labwareRowCount;
        const is384 = labwareWellCount === WellCount.WELL_384;

        let skipWellRow = 0;
        let skipWellColumn = 0;

        if (is384) {
          skipWellRow = 1;
          skipWellColumn = 1;
        }

        const rowTipCountNeeded = skipWellRow === 0 ? rowTipCount : rowTipCount + (rowTipCount - 1) * skipWellRow;
        const columnTipCountNeeded =
          skipWellColumn === 0 ? columnTipCount : columnTipCount + (columnTipCount - 1) * skipWellColumn;

        const taskWellRange = new WellItem(row, column, rowTipCountNeeded, columnTipCountNeeded);

        ({row: startRow, column: startColumn} = ensureWellRangeFitLabware(selectedLabware, taskWellRange));

        if (is384) {
          if (rowTipCount === 8) {
            startRow = startRow >= 2 ? 2 : 1;
          } else if (columnTipCount === 12) {
            startColumn = startColumn >= 2 ? 2 : 1;
          }
        }
      }

      const minTipPosition = dispatch(getMinTipPositionByLabwareId(labwareId));

      let task = {
        ID: taskId++,
        number,
        type,
        labwareID: labwareId,
        row: startRow,
        column: startColumn,
        volume: 0,
        rowTipCount,
        columnTipCount,
        enableMixing: false,
        mixingCycle: 3,
        mixingVolume: 0,
        tipPosition: minTipPosition,
        mixingPositionDispense: minTipPosition,
        mixingPositionAspirate: minTipPosition,
        speed: 3,
        retractSpeed: 20,
        mixingSpeed: 3,
        isIgnored: false,
        tipTouchOff: false,
        tipTouchOffDelay: 0,
        tipTouchOffHeight: selectedLabware?.wellDepth,
      };

      task = setDefaultTipTouchOffDirectionTasks(selectedLabware, task);

      let existingPipettingParam = {};

      if (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE) {
        const [alphabetRow, numericColumn] = dispatch(
          getWellTarget(task.labwareID, TipChannelType.TIP_CHANNEL_1, task.row, task.column),
        );

        // update aspirate task location
        if (task.type === TaskType.ASPIRATE) {
          if (existingTaskAspirate) {
            const newTaskAspirate = {
              ...existingTaskAspirate,
              row: alphabetRow,
              column: numericColumn,
              labwareID: labwareId,
            };

            dispatch(cardStateAction.restoreCardTaskStateById(newTaskAspirate));
            continue;
          }
        }

        // set pipetting parameter value based on existing dispense task
        if (existingTaskDispense && task.type === TaskType.DISPENSE) {
          existingPipettingParam = extractPipettingParameterFromTask(existingTaskDispense);
        } else if (existingTaskAspirate && task.type === TaskType.ASPIRATE) {
          existingPipettingParam = extractPipettingParameterFromTask(existingTaskAspirate);
        }

        task = dispatch(
          adjustTaskPipettingParametersByLabware(
            {
              ...task,
              row: alphabetRow,
              column: numericColumn,
              ...existingPipettingParam,
            },
            selectedLabware,
          ),
        );
      }

      dispatch(createBroadcastCard(cardId, isReuseTips));

      if (!isReuseTips && task.type === TaskType.DISPENSE) {
        if (!taskEjectTip && taskPickTip && existingTaskAspirate) {
          if (!taskIdsToRemove.includes(taskPickTip.ID)) {
            taskIdsToRemove.push(taskPickTip.ID);
          }
          if (!taskIdsToRemove.includes(existingTaskAspirate.ID)) {
            taskIdsToRemove.push(existingTaskAspirate.ID);
          }
        }

        // syncing aspirate task to dispense
        const {labwareID: aspirateLabwareID, column: aspirateColumn, row: aspirateRow} = existingTaskAspirate;
        const aspirateTaskLabware = find(labwareState, {LabwareID: aspirateLabwareID});
        const currentAspirateWellRange = new WellItem(aspirateRow, aspirateColumn, rowTipCount, columnTipCount);
        const fitWellRange = ensureWellRangeFitLabware(aspirateTaskLabware, currentAspirateWellRange);

        const syncedAspirateTask = {
          ...existingTaskAspirate,
          column: fitWellRange.column,
          row: fitWellRange.row,
        };

        taskListToAdd.push({
          ID: taskId++,
          type: TaskType.PICK_TIP,
          columnTipCount,
          rowTipCount,
          number: number++,
          labwareID: 2,
          isIgnored: existingTaskAspirate.isIgnored ?? false,
        });

        taskListToAdd.push({
          ...syncedAspirateTask,
          ID: taskId++,
          number: number++,
          rowTipCount,
          columnTipCount,
        });

        taskListToAdd.push({
          ...task,
          ID: taskId++,
          number: number++,
          volume: existingTaskDispense?.volume || task?.volume,
        });

        taskListToAdd.push({
          ID: taskId++,
          type: TaskType.EJECT_TIP,
          columnTipCount,
          rowTipCount,
          number: number++,
          labwareID: 0,
          changeTip: !isReuseTips,
          isIgnored: existingTaskAspirate.isIgnored ?? false,
        });
        continue;
      }

      if (!selectedCard || !existingTaskAspirate) {
        // add pick tip task if card is not exist
        const pickTipTask = {
          ID: taskId++,
          type: TaskType.PICK_TIP,
          columnTipCount,
          rowTipCount,
          row: '',
          column: '',
          number: number++,
          labwareID: 2,
        };
        taskListToAdd.push(pickTipTask);
        taskListToAdd.push({
          ...task,
          ID: taskId++,
          number: number++,
        });
      } else {
        taskListToAdd.push(task);

        if (task.type === TaskType.DISPENSE) {
          // sync pick tip task tip count
          if (taskPickTip.rowTipCount !== rowTipCount || taskPickTip.columnTipCount !== columnTipCount) {
            const newTaskPickTip = {
              ...taskPickTip,
              rowTipCount,
              columnTipCount,
            };

            dispatch(cardStateAction.restoreCardTaskStateById(newTaskPickTip));
          }
        }

        if (index === taskConfigList.length - 1) {
          // make sure eject tip task is always last
          if (taskEjectTip) {
            taskIdsToRemove.push(taskEjectTip.ID);
            // taskId += 2;
            taskEjectTip.ID = taskId++;
            taskEjectTip.columnTipCount = columnTipCount;
            taskEjectTip.rowTipCount = rowTipCount;
            taskEjectTip.changeTip = !isReuseTips;

            taskListToAdd.push(taskEjectTip);
          }
        }
      }
    }

    const dispenseLabwareIds = _.uniq(
      taskListToAdd.filter((task) => task.type === TaskType.DISPENSE).map((task) => task.labwareID),
    );

    batch(() => {
      if (taskListToAdd.length) {
        dispatch(cardStateAction.batchAddCardTask(cardId, cardName, CardType.BROADCAST, taskListToAdd));
      }
      if (taskIdsToRemove.length) {
        dispatch(cardStateAction.removeCardTasks(cardId, taskIdsToRemove));
      }
      if (dispenseLabwareIds.length) {
        dispatch(taskListBusinessAction.smarterDefaultDispenseTipPosition(dispenseLabwareIds));
      }
      dispatch(checkInvalidBroadcastCard(cardId));

      if (!skipSaveState) {
        dispatch(saveState());
      }
      dispatch(restoreTipCountForSingleCard(cardId));
    });
  };

export const addBroadcastDispenseTasks =
  (labwareId = 0, rowColumnList = [], rowTipCount = 1, columnTipCount = 1, skipSaveState = false) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    // const dispenseTasks = filterToArray(tasks,
    //   { ID: selectedCard.tasks, labwareID: labwareId, type: TaskType.DISPENSE });
    const dispenseTasks = Object.values(tasks).filter(
      (task) => task.labwareID === labwareId && task.type === TaskType.DISPENSE && selectedCard.tasks.includes(task.ID),
    );

    const taskIdsToRemove = [];
    const taskConfigList = [];

    batch(() => {
      for (let i = 0, ii = rowColumnList.length; i < ii; i += 1) {
        const r = rowColumnList[i];
        const {row, column, tipCount} = r;

        taskConfigList.push({
          type: TaskType.DISPENSE,
          labwareId,
          row,
          column,
          rowTipCount: tipCount ? tipCount.row : rowTipCount,
          columnTipCount: tipCount ? tipCount.column : columnTipCount,
        });

        // find duplicate task
        dispenseTasks.forEach((task) => {
          const dispenseTaskRow = convertRowToNumeric(task.row);
          const dispenseTaskColumn = task.column;

          if (dispenseTaskRow === row && dispenseTaskColumn === column) {
            taskIdsToRemove.push(task.ID);
          }
        });
      }

      dispatch(addBroadcastTask(taskConfigList, true));

      // remove duplicate task
      if (taskIdsToRemove.length) {
        dispatch(cardStateAction.removeCardTasks(selectedCard.ID, taskIdsToRemove));
      }

      if (!skipSaveState) {
        dispatch(saveState());
      }
    });
  };

export const removeBroadcastTask = (taskId) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;

  const selectedCard = find(cards, {isSelected: true});
  dispatch(cardStateAction.removeCardTask(selectedCard.ID, taskId));
  dispatch(saveState());
};

export const setBroadcastVolume = (volume) => (dispatch, getState) => {
  const {cardState, configState, protocolState} = getState();
  const {cards, tasks} = cardState;

  const selectedCard = find(cards, {isSelected: true});

  const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

  if (!selectedCard) {
    return;
  }

  const {ID: selectedCardId, name: selectedCardName, tasks: selectedCardTasks} = selectedCard;
  const isReuseTips = 'reuseTips' in selectedCard ? selectedCard.reuseTips : true;

  const selectedTasksAspirate = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.ASPIRATE});
  const selectedTasksDispense = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.DISPENSE});
  const selectedTasks = selectedTasksAspirate.concat(selectedTasksDispense);

  const dispenseLabwareIds = _.uniq(selectedTasksDispense.map((task) => task.labwareID));

  const maxTipVolume =
    protocolTrayType === TrayType.HIGH_VOLUME_CADDY
      ? configState.maxAspirateHighVolume.value
      : configState.maxAspirateVolume.value;

  const paramTasks = selectedTasks.map((e) => {
    let mixingVolume =
      // eslint-disable-next-line no-nested-ternary
      e.mixingVolume
        ? e.mixingVolume
        : e.type === TaskType.ASPIRATE && isReuseTips
          ? volume * selectedTasksDispense.length
          : volume;

    mixingVolume = parseFloat(mixingVolume);
    mixingVolume = mixingVolume > maxTipVolume ? maxTipVolume : mixingVolume;
    mixingVolume = parseFloat(parseFloat(mixingVolume).toFixed(1));

    let taskVolume = e.type === TaskType.ASPIRATE && isReuseTips ? volume * selectedTasksDispense.length : volume;
    taskVolume = parseFloat(parseFloat(taskVolume).toFixed(1));

    const {validatedVolume} = dispatch(validateMinVolumeAllowed(taskVolume));
    const {validatedVolume: validatedMixingVolume} = dispatch(validateMinVolumeAllowed(mixingVolume));

    return {
      id: e.ID,
      volume: validatedVolume,
      mixingVolume: validatedMixingVolume,
    };
  });

  batch(() => {
    dispatch(cardBroadcastStateAction.setBroadcastVolume(paramTasks));
    dispatch(taskListBusinessAction.smarterDefaultDispenseTipPosition(dispenseLabwareIds));
    dispatch(checkInvalidBroadcastCard(selectedCardId));

    const hasEjectTipTask = find(tasks, {ID: selectedCardTasks, type: TaskType.EJECT_TIP});

    if (hasEjectTipTask) {
      dispatch(restoreTipCountForSingleCard(selectedCardId));
      return;
    }

    const {rowTipCount, columnTipCount} = selectedTasks[0];

    const [, taskId, number] = generateCardIDAndTaskIDAndNumber(
      cards,
      tasks,
      CardType.BROADCAST,
      selectedCard?.ID,
      TaskType.EJECT_TIP,
      null,
      {row: rowTipCount, column: columnTipCount},
    );

    const ejectTipTask = {
      ID: taskId,
      type: TaskType.EJECT_TIP,
      columnTipCount,
      rowTipCount,
      row: '',
      column: '',
      number,
      labwareID: 2,
    };

    dispatch(cardStateAction.addCardTask(selectedCardId, selectedCardName, CardType.BROADCAST, taskId, ejectTipTask));
    dispatch(restoreTipCountForSingleCard(selectedCardId));
  });
};

export const backHandler = () => async (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard) {
    return;
  }

  const {ID: selectedCardId, tasks: selectedCardTasks} = selectedCard;

  const selectedTasksDispense = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.DISPENSE});
  const currentStep = selectedTasksDispense.length ? 3 : 2;

  if (currentStep === 2) {
    dispatch(taskListBusinessAction.deleteCard(selectedCardId));
    return;
  }

  const taskPickTip = find(tasks, {ID: selectedCardTasks, type: TaskType.PICK_TIP});
  const taskAspirate = find(tasks, {ID: selectedCardTasks, type: TaskType.ASPIRATE});
  const allTasksButFirstPickTipAndAspirate = selectedCard.tasks.filter(
    (id) => id !== taskPickTip.ID && id !== taskAspirate.ID,
  );

  dispatch(cardStateAction.removeCardTasks(selectedCardId, allTasksButFirstPickTipAndAspirate));
  dispatch(saveState());
};

export const cancelHandler = () => async (dispatch, getState) => {
  const {cardState} = getState();
  const {cards} = cardState;

  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard) {
    return;
  }

  const {ID: selectedCardId} = selectedCard;

  dispatch(taskListBusinessAction.deleteCard(selectedCardId));
};

export const editBroadcastTask =
  (taskParamList, skipSaveState = false) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards} = cardState;
    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    const taskList = [];

    taskParamList.forEach((task) => {
      const {row} = task;

      const newRow = row ? convertRowToAlphabet(row) : row;

      const taskParam = {
        ...task,
        row: newRow,
      };

      taskList.push(taskParam);
    });

    batch(() => {
      dispatch(cardStateAction.batchRestoreCardTaskStateById(taskList));
      dispatch(restoreTipCountForCards([selectedCard.ID]));

      if (!skipSaveState) {
        dispatch(saveState());
      }
    });
  };

export const saveBroadcastStateBeforeEdit = () => (dispatch, getState) => {
  const {cardState, labwareState} = getState();
  // never save task as selected
  const dummyCardState = JSON.parse(JSON.stringify(cardState));
  const taskKeys = Object.keys(dummyCardState.tasks);
  taskKeys.forEach((idx) => {
    dummyCardState.tasks[idx].isSelected = false;
  });
  dispatch(
    temporaryStateAction.addTemporaryState('broadcastStateBeforeEdit', {cardState: dummyCardState, labwareState}),
  );
};

export const restoreBroadcastStateAfterCancelEdit = () => (dispatch, getState) => {
  const {temporaryState} = getState();
  const {cardState, labwareState} = temporaryState.broadcastStateBeforeEdit;
  const {cards, tasks} = cardState;

  dispatch(temporaryStateAction.removeTemporaryState('broadcastStateBeforeEdit'));
  dispatch(cardStateAction.restoreCardState(cards));
  dispatch(cardStateAction.restoreCardTaskState(tasks));
  dispatch(labwareStateAction.restoreLabwareState(labwareState));
};

export const addAndRemoveBroadcastTask =
  (dispenseTasks, rowColumnListToAdd, rowColumnListToRemove, defaultCardId = null, removeCardAfterDelete = false) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = find(cards, {ID: defaultCardId}) ?? find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    const isReuseTips = 'reuseTips' in selectedCard ? selectedCard.reuseTips : true;

    const {ID: selectedCardId} = selectedCard;

    const sampleTask = dispenseTasks[0];

    if (rowColumnListToAdd.length) {
      dispatch(
        addBroadcastDispenseTasks(
          sampleTask.labwareID,
          rowColumnListToAdd,
          sampleTask.rowTipCount,
          sampleTask.columnTipCount,
          true,
        ),
      );
    }

    const taskIdsToRemove = [];
    const aspirateTasks = !isReuseTips
      ? filterToArray(tasks, {
          ID: selectedCard.tasks,
          type: TaskType.ASPIRATE,
        })
      : [];
    const pickTipTasks = !isReuseTips
      ? filterToArray(tasks, {
          ID: selectedCard.tasks,
          type: TaskType.PICK_TIP,
        })
      : [];
    const ejectTipTasks = !isReuseTips
      ? filterToArray(tasks, {
          ID: selectedCard.tasks,
          type: TaskType.EJECT_TIP,
        })
      : [];

    rowColumnListToRemove.forEach((item) => {
      const taskToRemove = dispenseTasks.filter(
        (task) => task.row.charCodeAt(0) - 64 === item.row && task.column === item.column,
      );
      if (taskToRemove[0]) {
        taskIdsToRemove.push(taskToRemove[0].ID);
        if (!isReuseTips) {
          const index = dispenseTasks.findIndex(
            (task) => task.row.charCodeAt(0) - 64 === item.row && task.column === item.column,
          );
          taskIdsToRemove.push(aspirateTasks[index]?.ID);
          taskIdsToRemove.push(pickTipTasks[index]?.ID);
          taskIdsToRemove.push(ejectTipTasks[index]?.ID);
        }
      }
    });

    dispatch(cardStateAction.removeCardTasks(selectedCardId, taskIdsToRemove));
    if (isReuseTips) dispatch(setBroadcastVolume(sampleTask.volume));
    if (
      removeCardAfterDelete &&
      ((selectedCard.tasks.length === taskIdsToRemove.length && !isReuseTips) ||
        (selectedCard.tasks.length === taskIdsToRemove.length + 3 && isReuseTips))
    ) {
      dispatch(cardStateAction.removeCard(selectedCardId));
    }
    dispatch(restoreTipCountForSingleCard(selectedCard.ID));
  };

export const batchEditBroadcastAspirateTasks =
  (newRow = 1, newColumn = 1, labwareId = 0, skipSaveState = false) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    const aspirateTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE});

    const rows = aspirateTasks.map((task) => task.row.charCodeAt(0) - 64);
    const columns = aspirateTasks.map((task) => task.column);

    const aspirateTaskRow = Math.min(...rows);
    const aspirateTaskColumn = Math.min(...columns);

    const rowDiff = aspirateTaskRow - newRow;
    const columnDiff = aspirateTaskColumn - newColumn;

    // eslint-disable-next-line
  const editedAspirateTasks = aspirateTasks.map((task) => {
      return {
        ...task,
        column: task.column - columnDiff,
        row: task.row.charCodeAt(0) - 64 - rowDiff,
        labwareID: labwareId,
      };
    });
    dispatch(editBroadcastTask(editedAspirateTasks, skipSaveState));
    dispatch(restoreTipCountForSingleCard(selectedCard.ID));
  };

export const batchEditBroadcastDispenseTasks =
  (newRow = 1, newColumn = 1, labwareId = 0, originLabware = {}) =>
  (dispatch, getState) => {
    const {cardState, labwareState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard) {
      return;
    }

    const isReuseTips = selectedCard.reuseTips;

    const labware = find(labwareState, {LabwareID: labwareId});
    const {colCount, rowCount} = labware;
    const is384 = colCount * rowCount === 384;

    let dispenseTasks = filterToArray(tasks, {
      ID: selectedCard.tasks,
      type: TaskType.DISPENSE,
      labwareID: originLabware.LabwareID,
    });
    dispenseTasks = sortTaskByWellPosition(dispenseTasks, true);
    const dispenseLabwareIds = _.uniq(dispenseTasks.map((task) => task.labwareID));
    const aspirateTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE});
    const sampleTask = dispenseTasks[0];

    let rows = dispenseTasks.map((task) => task.row.charCodeAt(0) - 64);
    let columns = dispenseTasks.map((task) => task.column);

    if (rowCount === 1 && colCount === 1) {
      rows = [sampleTask.row.charCodeAt(0) - 64];
      columns = [sampleTask.column];
    }

    const dispenseTaskRow = Math.min(...rows);
    const dispenseTaskColumn = Math.min(...columns);

    const rowDiff = dispenseTaskRow - newRow;
    const columnDiff = dispenseTaskColumn - newColumn;

    const editedTasks = [];
    const excessTask = [];

    // eslint-disable-next-line no-unsafe-optional-chaining
    const isOrigin384 = originLabware?.colCount * originLabware?.rowCount === 384;

    let newRowTipCount = Math.min(sampleTask.rowTipCount, originLabware.rowCount);
    let newColTipCount = Math.min(sampleTask.columnTipCount, originLabware.colCount);

    let rowIncrementer = is384 && !isOrigin384 ? 1 : 0;
    let colIncrementer = is384 && !isOrigin384 ? 1 : 0;

    let lastRow;
    let lastCol;

    const rowColumns = dispenseTasks.map((task) => ({row: task.row.charCodeAt(0) - 64, column: task.column}));
    const rightMostRow = rowColumns.reduce((prev, current) => (prev.row < current.row ? prev : current)).row;
    const topMostCol = rowColumns.reduce((prev, current) => (prev.column < current.column ? prev : current)).column;

    const columnList = [];
    const rowList = [];

    dispenseTasks.forEach((task) => {
      let pickTipTaskID;
      let aspirateTaskID;
      let ejectTipTaskID;

      if (!isReuseTips) {
        pickTipTaskID = task.ID - 2;
        aspirateTaskID = task.ID - 1;
        ejectTipTaskID = task.ID + 1;

        newRowTipCount = task.rowTipCount;
        newColTipCount = task.columnTipCount;

        const pickTipTask = tasks[pickTipTaskID];
        editedTasks.push({
          ...pickTipTask,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
        });
        const aspirateTask = tasks[aspirateTaskID];
        editedTasks.push({
          ...aspirateTask,
          row: aspirateTask.row.charCodeAt(0) - 64,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
        });
        const ejectTipTask = tasks[ejectTipTaskID];
        editedTasks.push({
          ...ejectTipTask,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
        });
      }
      const rowNumber = task.row.charCodeAt(0) - 64;
      const columnNumber = task.column;

      let rightMostRowDiff = Math.abs(rightMostRow - rowNumber);
      let topMostColDiff = Math.abs(topMostCol - columnNumber);

      if (isOrigin384 && !is384) {
        rightMostRowDiff /= 2;
        topMostColDiff /= 2;
      }

      if (lastRow !== rowNumber) {
        lastRow = rowNumber;
        if (rowNumber === rightMostRow && (is384 || isOrigin384)) {
          rowIncrementer = 0;
        } else {
          // eslint-disable-next-line no-nested-ternary
          rowIncrementer = (is384 && !isOrigin384 ? 1 : isOrigin384 && !is384 ? -1 : 0) * rightMostRowDiff;
        }
      }

      if (lastCol !== columnNumber) {
        lastCol = columnNumber;
        if (columnNumber === topMostCol && (is384 || isOrigin384)) {
          colIncrementer = 0;
        } else {
          // eslint-disable-next-line no-nested-ternary
          colIncrementer = (is384 && !isOrigin384 ? 1 : isOrigin384 && !is384 ? -1 : 0) * topMostColDiff;
        }
      }

      const newTaskColumn = colCount > 1 ? columnNumber - columnDiff + colIncrementer : 1;
      const newTaskRow = rowCount > 1 ? rowNumber - rowDiff + rowIncrementer : 1;

      if (
        newTaskColumn <= colCount &&
        newTaskRow <= rowCount &&
        !((colCount === 1 && rowList.includes(newTaskRow)) || (rowCount === 1 && columnList.includes(newTaskColumn)))
      ) {
        const dummy = {
          ...task,
          column: newTaskColumn,
          row: newTaskRow,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
          labwareID: labwareId,
        };

        editedTasks.push(dummy);
      } else {
        excessTask.push(task.ID);
        if (!isReuseTips) {
          excessTask.push(pickTipTaskID);
          excessTask.push(aspirateTaskID);
          excessTask.push(ejectTipTaskID);
        }
      }

      if (colCount === 1) {
        rowList.push(newTaskRow);
      }
      if (rowCount === 1) {
        columnList.push(newTaskColumn);
      }
    });

    if (isReuseTips && (newRowTipCount !== sampleTask.rowTipCount || newColTipCount !== sampleTask.columnTipCount)) {
      aspirateTasks.forEach((task) => {
        editedTasks.push({
          ...task,
          row: task.row.charCodeAt(0) - 64,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
        });
      });

      const pickTipsTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.PICK_TIP});
      pickTipsTasks.forEach((task) => {
        editedTasks.push({
          ...task,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
        });
      });

      const ejectTipsTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.EJECT_TIP});
      ejectTipsTasks.forEach((task) => {
        editedTasks.push({
          ...task,
          rowTipCount: newRowTipCount,
          columnTipCount: newColTipCount,
        });
      });
    }
    batch(() => {
      dispatch(editBroadcastTask(editedTasks, true));
      if (excessTask.length) {
        dispatch(cardStateAction.removeCardTasks(selectedCard.ID, excessTask));
      }
      dispatch(taskListBusinessAction.smarterDefaultDispenseTipPosition(dispenseLabwareIds));
      dispatch(restoreTipCountForSingleCard(selectedCard.ID));
    });
  };

export const setBroadcastAspirateVolume = (defaultAspirateVolume) => (dispatch, getState) => {
  const {cardState, configState, protocolState} = getState();
  const {cards, tasks} = cardState;

  const selectedCard = find(cards, {isSelected: true});
  if (!selectedCard) {
    return;
  }

  const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

  const selectedTasksAspirate = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE});
  const selectedTasksDispense = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.DISPENSE});

  const maxTipVolume =
    protocolTrayType === TrayType.HIGH_VOLUME_CADDY
      ? configState.maxAspirateHighVolume.value
      : configState.maxAspirateVolume.value;
  const volume = selectedTasksDispense[0]?.volume;

  const paramTasks = selectedTasksAspirate.map((e) => {
    let mixingVolume = defaultAspirateVolume;
    if (!defaultAspirateVolume) {
      mixingVolume = volume * selectedTasksDispense.length;
    }

    mixingVolume = parseFloat(mixingVolume);
    mixingVolume = mixingVolume > maxTipVolume ? maxTipVolume : mixingVolume;
    mixingVolume = parseFloat(parseFloat(mixingVolume).toFixed(1));

    let taskVolume = defaultAspirateVolume;
    if (!defaultAspirateVolume) {
      taskVolume = volume * selectedTasksDispense.length;
      taskVolume = parseFloat(parseFloat(taskVolume).toFixed(1));
    }

    return {
      id: e.ID,
      volume: taskVolume,
      mixingVolume,
    };
  });

  batch(() => {
    dispatch(cardBroadcastStateAction.setBroadcastVolume(paramTasks));
    dispatch(checkInvalidBroadcastCard(selectedCard.ID));
    dispatch(restoreTipCountForSingleCard(selectedCard.ID));
  });
};

const syncPipettingParameter = (oldDispenseTask) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard || selectedCard.type !== CardType.BROADCAST) {
    return;
  }

  const {tasks: selectedCardTasks} = selectedCard;

  const tasksDispense = Object.values(tasks).filter(
    (task) => selectedCardTasks.includes(task.ID) && task.ID === TaskType.DISPENSE,
  );

  if (!tasksDispense.length) {
    return;
  }

  const {
    enableMixing,
    mixingCycle,
    mixingVolume,
    tipPosition,
    mixingPositionAspirate,
    mixingPositionDispense,
    speed,
    retractSpeed,
    mixingSpeed,
  } = oldDispenseTask;

  const syncedTasks = tasksDispense.map((e) => ({
    ...e,
    enableMixing,
    mixingCycle,
    mixingVolume,
    tipPosition,
    mixingPositionAspirate,
    mixingPositionDispense,
    speed,
    retractSpeed,
    mixingSpeed,
  }));

  dispatch(cardStateAction.batchRestoreCardTaskStateById(syncedTasks));
};

export const optimizeBroadcastCard =
  (
    skipSetVolume = false,
    sourceLabwareBeforeMoving = null,
    skipSaveState = false,
    optimizeDestination = false,
    isReuseTips = false,
  ) =>
  (dispatch, getState) => {
    const {cardState, labwareState} = getState();
    const {cards, tasks} = cardState;

    const selectedCard = find(cards, {isSelected: true});

    if (!selectedCard || selectedCard.type !== CardType.BROADCAST) {
      return;
    }

    const {tasks: selectedCardTasks} = selectedCard;
    const allTask = Object.values(tasks).filter((task) => selectedCardTasks.includes(task.ID));
    const taskAspirate = allTask.filter((task) => task.type === TaskType.ASPIRATE);
    const tasksDispense = allTask.filter((task) => task.type === TaskType.DISPENSE);

    let taskGroup = [];
    let taskGroupByTipCount = {};

    if (!taskAspirate.length || !tasksDispense.length) {
      return;
    }

    const {
      rowTipCount: taskAspirateRowTipCount,
      columnTipCount: taskAspirateColumnTipCount,
      labwareID: taskAspirateLabwareId,
      column: taskAspirateColumn,
    } = taskAspirate[0];

    const {labwareID: taskDispenseLabwareId, volume: taskDispenseVolume} = tasksDispense[0];

    const sourceLabware = find(labwareState, {LabwareID: taskAspirateLabwareId});
    const destinationLabware = find(labwareState, {LabwareID: taskDispenseLabwareId});

    if (!sourceLabware || !destinationLabware) {
      return;
    }

    const {
      rowCount: sourceLabwareRowCount,
      colCount: sourceLabwareColumnCount,
      isSpearBio: sourceLabwareIsSpearBio,
      isTrough: sourceLabwareIsTrough,
    } = sourceLabware || {};

    let sourceLabwareBeforeMovingRowCount;
    let sourceLabwareBeforeMovingColumnCount;
    let sourceLabwareBeforeMovingIsTrough;

    if (sourceLabwareBeforeMoving) {
      sourceLabwareBeforeMovingRowCount = sourceLabwareBeforeMoving.rowCount;
      sourceLabwareBeforeMovingColumnCount = sourceLabwareBeforeMoving.colCount;
      sourceLabwareBeforeMovingIsTrough = sourceLabwareBeforeMoving.isTrough;
    }

    if (optimizeDestination) {
      const columnTipCount = sourceLabwareBeforeMovingColumnCount === 1 ? 1 : taskAspirateColumnTipCount;
      const rowTipCount = sourceLabwareBeforeMovingRowCount === 1 ? 1 : taskAspirateRowTipCount;

      const isSingleWellTrough = sourceLabwareColumnCount * sourceLabwareRowCount === 1;

      const maxDispenseRowTipCount = Math.max(...tasksDispense.map((task) => task.rowTipCount));
      const maxDispenseColumnTipCount = Math.max(...tasksDispense.map((task) => task.columnTipCount));

      const isOnSBTrough = sourceLabwareIsSpearBio && isOnSpearBioTrough(taskAspirateColumn, columnTipCount);

      const isColumnBased = isSingleWellTrough
        ? isColumnBasedSwipe({row: maxDispenseRowTipCount, column: maxDispenseColumnTipCount})
        : isLabwareColumnBased(sourceLabware);

      const isInitialLabwareColumnBased = sourceLabwareBeforeMoving
        ? isLabwareColumnBased(sourceLabwareBeforeMoving)
        : null;

      const canAcceptMultiTip = sourceLabwareIsTrough || isOnSBTrough;

      const isPreviouslyColumnBasedTrough = sourceLabwareBeforeMovingIsTrough && isInitialLabwareColumnBased;
      const isPreviouslyRowBasedTrough = sourceLabwareBeforeMovingIsTrough && !isInitialLabwareColumnBased;

      let forceToSplit = {
        row: Boolean(!canAcceptMultiTip && (taskAspirateRowTipCount === 1 || isPreviouslyColumnBasedTrough)),
        column: Boolean(!canAcceptMultiTip && (taskAspirateColumnTipCount === 1 || isPreviouslyRowBasedTrough)),
      };

      const splitBeforeGrouping = {row: 1, column: 1};

      const maxSizePerGroup = {
        row:
          !isSingleWellTrough &&
          (!isColumnBased ||
            // previous source labware is column-based and now its row-based
            (isInitialLabwareColumnBased && !isColumnBased))
            ? rowTipCount
            : null,
        column:
          !isSingleWellTrough &&
          (isColumnBased ||
            // previous source labware is row-based and now its column-based
            (!isInitialLabwareColumnBased && isColumnBased))
            ? columnTipCount
            : null,
      };

      // we want to generate the task with the direction of largest tip count number in a labware axis
      // that is, the opposite of the labware well direction.
      // it is done to optimize the number of tip count that will be used in one transfer
      let isGenerateColumnFirst = !isColumnBased;

      taskGroup = getTaskGroups(
        tasksDispense,
        labwareState,
        isGenerateColumnFirst,
        forceToSplit,
        splitBeforeGrouping,
        maxSizePerGroup,
      );

      if (
        isReuseTips &&
        (sourceLabwareRowCount === 1 || sourceLabwareColumnCount === 1 || isOnSBTrough) &&
        taskGroup.length > 1
      ) {
        const newDispenseRowTipCount = _.uniq(taskGroup.map((task) => task.rowTipCount));
        const newDispenseColumnTipCount = _.uniq(taskGroup.map((task) => task.columnTipCount));

        if (
          newDispenseRowTipCount.length > 1 ||
          newDispenseColumnTipCount.length > 1 ||
          (newDispenseRowTipCount[0] === 1 && newDispenseColumnTipCount[0] === 1)
        ) {
          isGenerateColumnFirst = sourceLabwareColumnCount !== 1;

          forceToSplit = {
            row: sourceLabwareRowCount === 1 || isOnSBTrough,
            column: sourceLabwareColumnCount === 1,
          };

          taskGroup = getTaskGroups(
            tasksDispense,
            labwareState,
            isGenerateColumnFirst,
            forceToSplit,
            splitBeforeGrouping,
            maxSizePerGroup,
          );
        }
      }

      taskGroupByTipCount = getTaskGroupByTipCountAndLabware(taskGroup, labwareState);
    } else {
      if (!skipSaveState) {
        dispatch(saveState());
      }
      return;
    }

    if (Object.keys(taskGroupByTipCount).length) {
      Object.values(taskGroupByTipCount).forEach((group) => {
        dispatch(
          addBroadcastDispenseTasks(
            group.labwareID,
            group.startingRowColumnList,
            group.tipCount.row,
            group.tipCount.column,
            true,
          ),
        );
      });

      if (!isReuseTips) {
        dispatch(cardStateAction.removeCardTasks(selectedCard.ID, selectedCard.tasks));
      } else {
        const dispenseTaskIds = tasksDispense.map((task) => task.ID);
        dispatch(cardStateAction.removeCardTasks(selectedCard.ID, dispenseTaskIds));
      }
    }

    dispatch(syncPipettingParameter(tasksDispense[0]));

    if (skipSetVolume) {
      if (!skipSaveState) {
        dispatch(saveState());
      }
      return;
    }

    dispatch(setBroadcastVolume(taskDispenseVolume));

    if (!skipSaveState) {
      dispatch(saveState());
    }
  };

export const onBroadcastReuseTipChange = (isReuseTips) => (dispatch, getState) => {
  const {cardState, configState, labwareState, protocolState} = getState();
  const {cards, tasks} = cardState;

  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard) {
    return;
  }
  const aspirateTask = find(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE});
  const aspirateLabware = find(labwareState, {LabwareID: aspirateTask.labwareID});

  if (!aspirateTask) {
    return;
  }

  const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

  dispatch(cardBroadcastStateAction.setCardBroadcastProperty(selectedCard.ID, isReuseTips));

  const maxTipVolume =
    protocolTrayType === TrayType.HIGH_VOLUME_CADDY
      ? configState.maxAspirateHighVolume.value
      : configState.maxAspirateVolume.value;

  const isTrough = isPositionOnTrough(aspirateLabware, aspirateTask);

  if (!isReuseTips) {
    const dispenseTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.DISPENSE});

    if (isTrough) {
      dispatch(optimizeBroadcastCard(false, null, true, true));
    } else {
      let taskVolume = aspirateTask.volume / dispenseTasks.length;
      taskVolume = parseFloat(parseFloat(taskVolume).toFixed(1));
      const mixingVolume = taskVolume > maxTipVolume ? maxTipVolume : taskVolume;
      dispatch(cardStateAction.removeAllCardTask(selectedCard.ID));
      const [, taskId] = generateCardIDAndTaskIDAndNumber(
        cards,
        tasks,
        CardType.BROADCAST,
        selectedCard?.ID,
        aspirateTask.type,
        null,
        {row: aspirateTask.rowTipCount, column: aspirateTask.columnTipCount},
      );
      let i = 0;
      const number = 1;
      const taskList = [];
      dispenseTasks.forEach((task) => {
        // add pick tip task
        taskList.push({
          ID: taskId + i,
          type: TaskType.PICK_TIP,
          columnTipCount: aspirateTask.columnTipCount,
          rowTipCount: aspirateTask.rowTipCount,
          row: '',
          column: '',
          number: number + i,
          labwareID: 2,
          isIgnored: aspirateTask.isIgnored ?? false,
        });

        // add aspirate task
        taskList.push({
          ...aspirateTask,
          ID: taskId + i + 1,
          number: number + i + 1,
          volume: taskVolume,
          mixingVolume,
          isSelected: false,
        });

        // add dispense task
        taskList.push({
          ...task,
          ID: taskId + i + 2,
          number: number + i + 2,
          isSelected: false,
        });

        // add eject tip task
        taskList.push({
          ID: taskId + i + 3,
          type: TaskType.EJECT_TIP,
          columnTipCount: aspirateTask.columnTipCount,
          rowTipCount: aspirateTask.rowTipCount,
          row: '',
          column: '',
          number: number + i + 3,
          labwareID: 2,
          isIgnored: aspirateTask.isIgnored ?? false,
          changeTip: true,
        });

        i += 4;
      });
      dispatch(cardStateAction.batchAddCardTask(selectedCard.ID, selectedCard.name, CardType.BROADCAST, taskList));
    }
  } else {
    // eslint-disable-next-line no-lonely-if
    if (isTrough) {
      dispatch(optimizeBroadcastCard(false, aspirateLabware, true, true, true));
    } else {
      const aspirateTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.ASPIRATE});
      const dispenseTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.DISPENSE});
      const pickTipTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.PICK_TIP});
      const ejectTipTasks = filterToArray(tasks, {ID: selectedCard.tasks, type: TaskType.EJECT_TIP});

      const aspirateLength = aspirateTasks.length;
      if (aspirateLength <= 0) {
        return;
      }

      let taskVolume = aspirateTasks[0].volume * aspirateLength;
      taskVolume = parseFloat(parseFloat(taskVolume).toFixed(1));
      const mixingVolume = taskVolume > maxTipVolume ? maxTipVolume : taskVolume;
      let {number} = aspirateTasks[0];
      dispatch(cardStateAction.setTaskMixingVolume(aspirateTasks[0].ID, mixingVolume));
      dispatch(cardStateAction.setTaskVolume(aspirateTasks[0].ID, taskVolume, aspirateTasks[0].type));
      const ejectTipTask = ejectTipTasks[ejectTipTasks.length - 1];
      ejectTipTask.changeTip = false;
      dispatch(cardStateAction.restoreCardTaskStateById(ejectTipTask));

      const taskToRemove = [];
      if (aspirateTasks.length > 1) {
        aspirateTasks.shift();
        aspirateTasks.forEach((task) => {
          taskToRemove.push(task.ID);
        });
      }

      if (pickTipTasks.length > 1) {
        pickTipTasks.shift();
        pickTipTasks.forEach((task) => {
          taskToRemove.push(task.ID);
        });
      }

      if (ejectTipTasks.length > 1) {
        ejectTipTasks.pop();
        ejectTipTasks.forEach((task) => {
          taskToRemove.push(task.ID);
        });
      }

      if (taskToRemove.length > 0) {
        dispatch(cardStateAction.removeCardTasks(selectedCard?.ID, taskToRemove));
      }

      // update task number
      dispenseTasks.forEach((task) => {
        dispatch(cardStateAction.setTaskNumber(task.ID, number + 1));
        number += 1;
      });

      dispatch(cardStateAction.setTaskNumber(ejectTipTask.ID, number + 1));
    }
  }
};

export const resizeBroadcastTask = (oldTSLayout, newTSLayout) => (dispatch, getState) => {
  const {cardState} = getState();
  const {cards, tasks} = cardState;

  const selectedCard = find(cards, {isSelected: true});

  if (!selectedCard || selectedCard.type !== CardType.BROADCAST) {
    return;
  }

  const {tasks: selectedCardTasks} = selectedCard;

  const taskPickTip = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.PICK_TIP});
  const taskEjectTip = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.EJECT_TIP});
  const tasksAspirate = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.ASPIRATE});
  const tasksDispense = filterToArray(tasks, {ID: selectedCardTasks, type: TaskType.DISPENSE});

  const oldOneDmnsnLayout = find(oldTSLayout, {type: TransformSelectionType.ONE_DIMENSION});
  const oldTwoDmnsnLayout = find(oldTSLayout, {type: TransformSelectionType.TWO_DIMENSION});
  const newOneDmnsnLayout = find(newTSLayout, {type: TransformSelectionType.ONE_DIMENSION});
  const newTwoDmnsnLayout = find(newTSLayout, {type: TransformSelectionType.TWO_DIMENSION});

  const taskList = [];
  const taskListToAdd = [];
  const taskListToRemove = [];

  // change pattern and remapping task
  if (newTSLayout.isMapping) {
    const {size: newSizeAspirate, start: newStartAspirate} = newOneDmnsnLayout;
    const {start: newStartDispense, size: newSizeDispense, labware: dispenseLabware} = newTwoDmnsnLayout;

    const is384 = dispenseLabware.colCount * dispenseLabware.rowCount === LabwareWellCount.LABWARE_384;

    const taskIdsToRemove = [];

    // syncing tip count in pick tip task
    taskPickTip.forEach((task) => {
      taskList.push({
        ...task,
        rowTipCount: newOneDmnsnLayout.tipCount.row,
        columnTipCount: newOneDmnsnLayout.tipCount.column,
      });
    });

    // syncing tip count in eject tip task
    taskEjectTip.forEach((task) => {
      taskList.push({
        ...task,
        rowTipCount: newOneDmnsnLayout.tipCount.row,
        columnTipCount: newOneDmnsnLayout.tipCount.column,
      });
    });
    // syncing tip count in aspirate task
    tasksAspirate.forEach((task) => {
      taskList.push({
        ...task,
        row: newStartAspirate.row,
        column: newStartAspirate.column,
        rowTipCount: newOneDmnsnLayout.tipCount.row,
        columnTipCount: newOneDmnsnLayout.tipCount.column,
      });
    });

    // mapping task
    if (newSizeAspirate.row === 1) {
      // mapping row based task to column based task
      tasksDispense.forEach((task, idx) => {
        const newRow = newStartDispense.row + (is384 ? 2 * idx : idx);
        const max = is384 ? Math.ceil(newSizeDispense.row / 2) : newSizeDispense.row;

        if (
          idx < max &&
          newRow <= dispenseLabware.rowCount &&
          (newStartDispense.row >= newStartAspirate.row || newRow < newStartAspirate.row)
        ) {
          taskList.push({
            ...task,
            row: newRow,
            column: newStartDispense.column,
            rowTipCount: newOneDmnsnLayout.tipCount.row,
            columnTipCount: newOneDmnsnLayout.tipCount.column,
          });
        } else {
          taskIdsToRemove.push(task.ID);
        }
      });
    } else if (newSizeAspirate.column === 1) {
      // mapping column based task to row based task
      tasksDispense.forEach((task, idx) => {
        const newColumn = newStartDispense.column + (is384 ? 2 * idx : idx);
        const max =
          is384 && newSizeDispense.column > 1 ? Math.ceil(newSizeDispense.column / 2) : newSizeDispense.column;

        if (
          idx < max &&
          newColumn <= dispenseLabware.colCount &&
          (newStartDispense.column >= newStartAspirate.column || newColumn < newStartAspirate.column)
        ) {
          taskList.push({
            ...task,
            row: newStartDispense.row,
            column: newColumn,
            rowTipCount: newOneDmnsnLayout.tipCount.row,
            columnTipCount: newOneDmnsnLayout.tipCount.column,
          });
        } else {
          taskIdsToRemove.push(task.ID);
        }
      });
    }

    batch(() => {
      dispatch(editBroadcastTask(taskList, true));
      // remove exces dispense task
      dispatch(cardStateAction.removeCardTasks(selectedCard.ID, taskIdsToRemove));
    });
    return;
  }

  // add/remove task
  if (newTSLayout.isAddOrRemoved) {
    const {start: oldStart, size: oldSize} = oldTwoDmnsnLayout;
    const {start: newStart, size: newSize, labware} = newTwoDmnsnLayout;

    const isLabware384 = labware.rowCount * labware.colCount === LabwareWellCount.LABWARE_384;
    const isBroadcastSingleWellDestination =
      newOneDmnsnLayout.size.row === 1 &&
      newOneDmnsnLayout.size.column === 1 &&
      newOneDmnsnLayout.labware.rowCount > 1 &&
      newOneDmnsnLayout.labware.colCount > 1;

    const rowDiff = Math.abs(newSize.row - oldSize.row);
    const columnDiff = Math.abs(newSize.column - oldSize.column);

    const maxRow = isBroadcastSingleWellDestination ? newSize.row : 1;
    const maxColumn = isBroadcastSingleWellDestination ? newSize.column : 1;

    if (oldStart.row !== newStart.row) {
      // resize with handle e
      if (oldStart.row > newStart.row) {
        // increment
        for (let i = 0; i < rowDiff; i += 1) {
          if (isLabware384 && ((i % 2 === 1 && rowDiff % 2 === 0) || (i % 2 === 0 && rowDiff % 2 === 1))) {
            continue;
          }

          const newRow = newStart.row + i;

          for (let j = 0; j < maxColumn; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }
            const newColumn = oldStart.column + j;

            taskListToAdd.push({row: newRow, column: newColumn});
          }
        }
      } else if (oldStart.row < newStart.row) {
        // decrement
        for (let i = 0; i < rowDiff; i += 1) {
          if (isLabware384 && i % 2 === 1) {
            continue;
          }

          const newRow = oldStart.row + i;

          for (let j = 0; j < maxColumn; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newColumn = oldStart.column + j;

            taskListToRemove.push({row: newRow, column: newColumn});
          }
        }
      }
    } else if (oldStart.column !== newStart.column) {
      // resize with handle n
      if (oldStart.column > newStart.column) {
        // increment
        for (let i = 0; i < columnDiff; i += 1) {
          if (isLabware384 && ((i % 2 === 1 && columnDiff % 2 === 0) || (i % 2 === 0 && columnDiff % 2 === 1))) {
            continue;
          }
          const newColumn = newStart.column + i;

          for (let j = 0; j < maxRow; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newRow = oldStart.row + j;

            taskListToAdd.push({row: newRow, column: newColumn});
          }
        }
      } else if (oldStart.column < newStart.column) {
        // decrement
        for (let i = 0; i < columnDiff; i += 1) {
          if (isLabware384 && i % 2 === 1) {
            continue;
          }

          const newColumn = oldStart.column + i;

          for (let j = 0; j < maxRow; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newRow = oldStart.row + j;

            taskListToRemove.push({row: newRow, column: newColumn});
          }
        }
      }
    } else if (oldSize.row !== newSize.row) {
      // resize with handle w
      if (oldSize.row < newSize.row) {
        // increment
        for (let i = 0; i < rowDiff; i += 1) {
          if (isLabware384 && i % 2 === 0) {
            continue;
          }

          const newRow = oldStart.row + oldSize.row + i;

          for (let j = 0; j < maxColumn; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newColumn = oldStart.column + j;

            taskListToAdd.push({row: newRow, column: newColumn});
          }
        }
      } else if (oldSize.row > newSize.row) {
        // decrement
        for (let i = 1; i <= rowDiff; i += 1) {
          if (isLabware384 && i % 2 === 0) {
            continue;
          }

          const newRow = oldStart.row + oldSize.row - i;

          for (let j = 0; j < maxColumn; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newColumn = oldStart.column + j;

            taskListToRemove.push({row: newRow, column: newColumn});
          }
        }
      }
    } else if (oldSize.column !== newSize.column) {
      // resize with handle s
      if (oldSize.column < newSize.column) {
        // increment
        for (let i = 0; i < columnDiff; i += 1) {
          if (isLabware384 && i % 2 === 0) {
            continue;
          }

          const newColumn = oldStart.column + oldSize.column + i;

          for (let j = 0; j < maxRow; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newRow = oldStart.row + j;

            taskListToAdd.push({row: newRow, column: newColumn});
          }
        }
      } else if (oldSize.column > newSize.column) {
        // decrement
        for (let i = 1; i <= columnDiff; i += 1) {
          if (isLabware384 && i % 2 === 0) {
            continue;
          }
          const newColumn = oldStart.column + oldSize.column - i;

          for (let j = 0; j < maxRow; j++) {
            if (isLabware384 && j % 2 === 1) {
              continue;
            }

            const newRow = oldStart.row + j;

            taskListToRemove.push({row: newRow, column: newColumn});
          }
        }
      }
    }

    dispatch(addAndRemoveBroadcastTask(tasksDispense, taskListToAdd, taskListToRemove));
    return;
  }

  // increase/decrease tip count
  const diffRowAspirate = newOneDmnsnLayout.size.row - oldOneDmnsnLayout.size.row;
  const diffColumnAspirate = newOneDmnsnLayout.size.column - oldOneDmnsnLayout.size.column;

  const isNewStartAspirate =
    newOneDmnsnLayout.start.row !== oldOneDmnsnLayout.start.row ||
    newOneDmnsnLayout.start.column !== oldOneDmnsnLayout.start.column;

  const {labware: labwareAspirate} = newOneDmnsnLayout;

  tasksAspirate.forEach((task) => {
    let newRow = convertRowToNumeric(task.row) - diffRowAspirate;
    let newColumn = task.column - diffColumnAspirate;

    // normalize newRow and newColumn
    newRow = newRow < 1 || labwareAspirate.rowCount === 1 ? newOneDmnsnLayout.start.row : newRow;
    newColumn = newColumn < 1 || labwareAspirate.colCount === 1 ? newOneDmnsnLayout.start.column : newColumn;

    taskList.push({
      ...task,
      row: isNewStartAspirate ? newRow : convertRowToNumeric(task.row),
      column: isNewStartAspirate ? newColumn : task.column,
      rowTipCount: newOneDmnsnLayout.tipCount.row,
      columnTipCount: newOneDmnsnLayout.tipCount.column,
    });
  });

  const diffRowDispense = newTwoDmnsnLayout.size.row - oldTwoDmnsnLayout.size.row;
  const diffColumnDispense = newTwoDmnsnLayout.size.column - oldTwoDmnsnLayout.size.column;

  const isNewStartDispense =
    newTwoDmnsnLayout.start.row !== oldTwoDmnsnLayout.start.row ||
    newTwoDmnsnLayout.start.column !== oldTwoDmnsnLayout.start.column;

  const {labware: dispenseLabware} = newTwoDmnsnLayout;

  tasksDispense.forEach((task) => {
    let newRow = convertRowToNumeric(task.row) - diffRowDispense;
    let newColumn = task.column - diffColumnDispense;

    // normalize newRow and newColumn
    newRow = newRow < 1 || dispenseLabware.rowCount === 1 ? newTwoDmnsnLayout.start.row : newRow;
    newColumn = newColumn < 1 || dispenseLabware.colCount === 1 ? newTwoDmnsnLayout.start.column : newColumn;

    taskList.push({
      ...task,
      row: isNewStartDispense ? newRow : convertRowToNumeric(task.row),
      column: isNewStartDispense ? newColumn : task.column,
      rowTipCount: newTwoDmnsnLayout.tipCount.row,
      columnTipCount: newTwoDmnsnLayout.tipCount.column,
    });
  });

  // syncing tip count in pick tip task
  taskPickTip.forEach((task) => {
    taskList.push({
      ...task,
      rowTipCount: newOneDmnsnLayout.tipCount.row,
      columnTipCount: newOneDmnsnLayout.tipCount.column,
    });
  });

  // syncing tip count in eject tip task
  taskEjectTip.forEach((task) => {
    taskList.push({
      ...task,
      rowTipCount: newOneDmnsnLayout.tipCount.row,
      columnTipCount: newOneDmnsnLayout.tipCount.column,
    });
  });

  dispatch(editBroadcastTask(taskList, true));
};

export const duplicateBroadcastTaskOnPlateSwap =
  (card, tasks, labwareID, rowShift = 0, columnShift = 0, taskToShiftIDs = []) =>
  (dispatch) => {
    const shiftWell = (value, shifter, ID) => (!taskToShiftIDs.includes(ID) ? value : value * 2 - 1 + shifter);

    const sourceBroadcast = find(tasks, {ID: card.tasks, type: TaskType.ASPIRATE});
    if (sourceBroadcast.labwareID === labwareID) {
      // duplicate a whole card
      dispatch(cardStateAction.setAllCardIsSelected(false));
      dispatch(cardStateAction.setAllTaskIsSelected(false));

      const sampleAspirateTask = {
        ...sourceBroadcast,
        labwareId: sourceBroadcast.labwareID,
        row: shiftWell(convertRowToNumeric(sourceBroadcast.row), rowShift, sourceBroadcast.ID),
        column: shiftWell(sourceBroadcast.column, columnShift, sourceBroadcast.ID),
      };
      dispatch(addBroadcastTask([sampleAspirateTask], true, true, card.reuseTips));

      const existTasks = filterToArray(tasks, {ID: card.tasks, type: TaskType.DISPENSE});
      const taskConfigList = existTasks.map((task) => ({
        ...task,
        labwareId: task.labwareID,
        row: shiftWell(convertRowToNumeric(task.row), rowShift, task.ID),
        column: shiftWell(task.column, columnShift, task.ID),
      }));
      dispatch(addBroadcastTask(taskConfigList, true));
      dispatch(setBroadcastVolume(existTasks[0].volume));
    } else {
      const existTasks = filterToArray(tasks, {ID: card.tasks, labwareID});
      const taskConfigList = existTasks.map((task) => ({
        ...task,
        labwareId: task.labwareID,
        row: shiftWell(convertRowToNumeric(task.row), rowShift, task.ID),
        column: shiftWell(task.column, columnShift, task.ID),
      }));
      dispatch(addBroadcastTask(taskConfigList, true, card.ID));
      if (card.reuseTips) dispatch(setBroadcastVolume(existTasks[0].volume));
    }
  };

export const handlePlateSwapDuplicateInBroadcast =
  (labwareID, choosenQuadrant, quadrantShifter) => (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const broadcastCards = filterToArray(cards, {type: CardType.BROADCAST});
    broadcastCards.forEach((card) => {
      const existBroadcastTasks = filterToArray(tasks, {ID: card.tasks, labwareID});
      if (existBroadcastTasks.length) {
        choosenQuadrant.forEach((q) => {
          dispatch(
            duplicateBroadcastTaskOnPlateSwap(
              card,
              tasks,
              labwareID,
              quadrantShifter[q].row,
              quadrantShifter[q].column,
              existBroadcastTasks.map((task) => task.ID),
            ),
          );
        });
      }
    });
  };

export const handlePlateSwapMergeInBroadcast =
  (
    labwareID,
    // eslint-disable-next-line default-param-last
    shiftWell = () => {},
    defaultQuadrant,
  ) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    let processedIds = [];

    const broadcastCards = filterToArray(cards, {type: CardType.BROADCAST});
    broadcastCards.forEach((card) => {
      const existBroadcastTasks = filterToArray(tasks, {ID: card.tasks, labwareID});
      const existDispenseBroadcastTasks = filterToArray(tasks, {ID: card.tasks, labwareID, type: TaskType.DISPENSE});
      const taskIdsToRemove = [];
      const movedTasks = [];

      processedIds = [...processedIds, ...card.tasks];
      let removeCard = false;

      existBroadcastTasks.forEach((task) => {
        let newRow = convertRowToNumeric(task.row);
        let newColumn = task.column;
        const quadrant = getQuadrant(newRow, newColumn);
        if (defaultQuadrant && quadrant === defaultQuadrant) {
          [newRow, newColumn] = shiftWell(newRow, newColumn);
          movedTasks.push({
            ...task,
            row: convertRowToAlphabet(newRow),
            column: newColumn,
          });
        } else if (task.type === TaskType.DISPENSE) {
          taskIdsToRemove.push({row: convertRowToNumeric(task.row), column: task.column});
        } else {
          removeCard = true;
        }
      });

      if (removeCard) {
        dispatch(cardStateAction.removeCard(card.ID));
      } else {
        dispatch(cardStateAction.batchRestoreCardTaskStateById(movedTasks));
        dispatch(addAndRemoveBroadcastTask(existDispenseBroadcastTasks, [], taskIdsToRemove, card.ID, true));
      }
    });

    return processedIds;
  };

export const onFinishedBroadcastWizard = () => (dispatch, getState) => {
  const {labwareState, cardState, temporaryState} = getState();
  const {broadcastStateBeforeEdit} = temporaryState;

  const selectedCard = getFirstSelectedCard(cardState.cards);

  dispatch(setVolumeModalOpen(false));
  dispatch(setBroadcastTaskEditStep(null));
  dispatch(setSelectedMenu(ProtocolEditorViewMode.SIMPLE_TRANSFER));

  const taskAspirate = getBroadcastTaskByType(selectedCard, cardState.tasks, TaskType.ASPIRATE);
  const taskAspirateLabware = find(labwareState, {LabwareID: taskAspirate?.labwareID});
  const isAspirateLabwareTrough = isPositionOnTrough(taskAspirateLabware, taskAspirate);
  const isReuseTips = selectedCard && 'reuseTips' in selectedCard ? selectedCard.reuseTips : false;

  dispatch(optimizeBroadcastCard(false, null, true, isAspirateLabwareTrough, isReuseTips));
  dispatch(temporaryStateAction.removeTemporaryState(BROADCAST_STATE_BEFORE_EDIT));

  if (broadcastStateBeforeEdit) {
    dispatch(
      showSnackbar(Feedback.SUCCESS, intl.formatMessage({id: 'protocolEditor.broadcast.editTask.successMessage'})),
    );
  }
  dispatch(restoreTipCountForCards([selectedCard.ID]));
};
